import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { withTimeout } from '../lib/fetchWithTimeout';
import { logAccess } from '../lib/db';

const AuthContext = createContext();
const PROFILE_TIMEOUT_MS = 15000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // True when the session came from an invite link — must set password before entering app.
  // Read from sessionStorage, which was written by the inline script in index.html
  // BEFORE Supabase had a chance to clear the URL hash.
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(
    () => sessionStorage.getItem('sb_pw_setup') === '1'
  );
  // Prevents AppWrapper from rendering the app during the async login() check.
  // onAuthStateChange fires SIGNED_IN immediately after signInWithPassword,
  // which would flip isAuthenticated=true before the deactivation check runs.
  const [loginInProgress, setLoginInProgress] = useState(false);

  // Check if user is logged in on mount
  useEffect(() => {
    let cancelled = false;

    const loadProfile = async (userId) => {
      const { data: profile, error: profileError } = await withTimeout(
        supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .maybeSingle(),
        PROFILE_TIMEOUT_MS
      );

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        return null;
      }

      return profile;
    };

    // Hard timeout so the loading spinner can never get stuck forever
    // (e.g. if Supabase is unreachable, paused, or misconfigured).
    // 60s timeout — Supabase free tier can take up to 30s to wake from
    // inactivity (cold start). 60s gives it plenty of time while still
    // eventually recovering if the project is truly unreachable.
    const loadingTimeout = setTimeout(() => {
      if (cancelled) return;
      console.warn('[AuthContext] Supabase session check timed out after 60s. Falling through to login page. Check Supabase URL/key and network.');
      setError('Connection timed out. Please refresh the page — your Supabase project may be waking up from sleep.');
      setLoading(false);
    }, 60000);

    const checkUser = async () => {
      try {
        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user || null);

        // Fetch user profile
        if (session?.user) {
          const profile = await loadProfile(session.user.id);
          if (!cancelled) {
            setUserProfile(profile);
          }
        }
      } catch (err) {
        console.error('[AuthContext] Error checking user:', err);
        setError(err.message);
      } finally {
        clearTimeout(loadingTimeout);
        if (!cancelled) setLoading(false);
      }
    };

    checkUser();

    // Listen for auth changes.
    // Skip INITIAL_SESSION — checkUser() above already handles the startup
    // session via getSession(), so letting INITIAL_SESSION run too would cause
    // a duplicate profile fetch and trigger FirmContext to reload twice,
    // unmounting every page component mid-render.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'INITIAL_SESSION') return;

        // If sessionStorage still has the invite flag when SIGNED_IN fires,
        // re-assert needsPasswordSetup. This covers the race where checkUser()
        // resolved before detectSessionInUrl finished, so the component rendered
        // with isAuthenticated=false and needsPasswordSetup=true — but then
        // SIGNED_IN fired, flipping isAuthenticated to true without re-checking
        // the flag, and the app routed past SetPasswordScreen.
        if (event === 'SIGNED_IN' && sessionStorage.getItem('sb_pw_setup') === '1') {
          setNeedsPasswordSetup(true);
          // Also capture tokens from the live session in case the inline
          // script ran but the hash was in PKCE format (no access_token in URL).
          if (session?.access_token && !sessionStorage.getItem('sb_invite_at')) {
            sessionStorage.setItem('sb_invite_at', session.access_token);
          }
          if (session?.refresh_token && !sessionStorage.getItem('sb_invite_rt')) {
            sessionStorage.setItem('sb_invite_rt', session.refresh_token);
          }
        }

        const authUser = session?.user || null;
        setUser(authUser);

        if (authUser) {
          setUserProfile((prev) => (prev?.id === authUser.id ? prev : null));
          setTimeout(() => {
            loadProfile(authUser.id)
              .then((profile) => {
                if (!cancelled) setUserProfile(profile);
              })
              .catch((err) => {
                console.error('[AuthContext] Error refreshing profile:', err);
              });
          }, 0);
        } else {
          setUserProfile(null);
        }
      }
    );

    return () => {
      cancelled = true;
      clearTimeout(loadingTimeout);
      subscription?.unsubscribe();
    };
  }, []);

  const register = async (email, password, firstName, lastName, role = null) => {
    try {
      setError(null);
      // NOTE: intentionally NOT calling setLoading(true) here.
      // AuthContext.loading controls the full-page spinner in AppWrapper, which
      // unmounts LoginPage. If we set it true here, LoginPage is destroyed before
      // it can display the error/success message. LoginPage handles its own
      // isLoading spinner independently.

      // If no explicit role given: registering without an invite code means
      // creating a brand new firm — this user is the Admin. An invite code
      // means joining an existing firm as a regular member; role will be
      // set by the invite flow, so default to Associate.
      const hasInvite = !!sessionStorage.getItem('inviteCode');
      const effectiveRole = role ?? (hasInvite ? 'Associate' : 'Admin');

      // Pass name/role as metadata so the DB trigger (handle_new_auth_user)
      // can create the public.users row automatically — even before email
      // confirmation, bypassing RLS entirely.
      //
      // emailRedirectTo is REQUIRED. Without it, Supabase falls back to the
      // project's Dashboard "Site URL" setting, which is easy to misconfigure
      // (e.g. left as localhost during dev, or stale after a domain change).
      // Using window.location.origin makes the redirect self-healing: it
      // always points back to wherever the user actually signed up from,
      // regardless of what's in the Dashboard. The Dashboard's "Redirect
      // URLs" allowlist must still include this origin — see
      // OPERATOR_RUNBOOK.md → "Supabase Auth URL configuration."
      const { data: { user: newUser }, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            first_name: firstName,
            last_name:  lastName,
            role: effectiveRole,
          },
        },
      });

      if (signUpError) throw signUpError;

      // Fallback: if no DB trigger exists yet, try the manual insert.
      // The trigger uses ON CONFLICT DO NOTHING, so running both is safe.
      if (newUser?.id) {
        await supabase.from('users').insert([{
          id:         newUser.id,
          email,
          first_name: firstName,
          last_name:  lastName,
          role: effectiveRole,
          status:     'Active',
        }]).select(); // ignore error — trigger may have already created the row
      }

      return newUser;
    } catch (err) {
      const message = err.message || 'Registration failed';
      setError(message);
      throw err;
    }
  };

  const login = async (email, password) => {
    try {
      setLoginInProgress(true);
      setError(null);
      // NOTE: intentionally NOT calling setLoading(true) here — same reason as
      // register(). AppWrapper hides LoginPage while loading=true, which would
      // destroy it before any error message can be shown.

      const { data: { user }, error: loginError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (loginError) throw loginError;

      // Fetch user profile — use maybeSingle so missing rows don't throw.
      const { data: profile } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      // Block deactivated employees from logging in.
      // Only applies to users created through the employee-access flow — they
      // have employee_id set in their profile (e.g. "EMP000001"). Firm owners
      // who registered normally never have employee_id, so they are exempt.
      if (profile?.employee_id) {
        const { data: empRecord } = await supabase
          .from('employees')
          .select('status')
          .eq('user_id', user.id)
          .maybeSingle();

        const isDeactivated =
          profile?.status === 'inactive' ||
          empRecord?.status === 'inactive';

        if (isDeactivated) {
          // Revoke the fresh session so nothing lingers in localStorage.
          await supabase.auth.signOut();
          throw new Error('Your account has been deactivated. Please contact your firm administrator.');
        }
      }

      setUser(user);
      setUserProfile(profile);

      // Update last login (fire-and-forget)
      supabase
        .from('users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', user.id);

      logAccess('login');

      return user;
    } catch (err) {
      const message = err.message || 'Login failed';
      setError(message);
      throw err;
    } finally {
      setLoginInProgress(false);
    }
  };

  const logout = async () => {
    try {
      setError(null);
      // Log BEFORE signOut — RPC requires an authenticated session.
      logAccess('logout');
      const { error: logoutError } = await supabase.auth.signOut();
      if (logoutError) throw logoutError;

      setUser(null);
      setUserProfile(null);
    } catch (err) {
      setError(err.message || 'Logout failed');
      throw err;
    }
  };

  const resetPassword = async (email) => {
    try {
      setError(null);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
    } catch (err) {
      setError(err.message || 'Password reset failed');
      throw err;
    }
  };

  const updateProfile = async (updates) => {
    try {
      setError(null);
      const { error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      setUserProfile({ ...userProfile, ...updates });
    } catch (err) {
      setError(err.message || 'Profile update failed');
      throw err;
    }
  };

  const refreshProfile = async () => {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) return;
    const { data: profile } = await supabase
      .from('users')
      .select('*')
      .eq('id', currentUser.id)
      .maybeSingle();
    if (profile) setUserProfile(profile);
  };

  const completePasswordSetup = async (password) => {
    // Re-establish the session from the stored invite tokens before calling
    // updateUser — this prevents "Auth session missing" if detectSessionInUrl
    // hasn't persisted the session to memory yet.
    const at = sessionStorage.getItem('sb_invite_at');
    const rt = sessionStorage.getItem('sb_invite_rt');
    if (at) {
      const { error: sessionErr } = await supabase.auth.setSession({
        access_token:  at,
        refresh_token: rt || '',
      });
      if (sessionErr) throw sessionErr;
    }

    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) throw err;

    sessionStorage.removeItem('sb_pw_setup');
    sessionStorage.removeItem('sb_invite_at');
    sessionStorage.removeItem('sb_invite_rt');
    setNeedsPasswordSetup(false);
    logAccess('password_setup');
  };

  // Called by ChangePasswordPage after a successful password update.
  // Directly patches local state so the UI unblocks immediately, regardless
  // of whether the DB update or the USER_UPDATED auth event has completed.
  const markPasswordChanged = () => {
    setUserProfile((prev) => prev ? { ...prev, must_change_password: false } : prev);
    logAccess('password_change');
  };

  const value = {
    user,
    userProfile,
    loading,
    error,
    register,
    login,
    logout,
    resetPassword,
    updateProfile,
    refreshProfile,
    completePasswordSetup,
    markPasswordChanged,
    needsPasswordSetup,
    loginInProgress,
    isAuthenticated: !!user,
    // True when the user is a firm employee logging in for the first time
    // with a system-generated password — they must change it before proceeding.
    mustChangePassword: !!userProfile?.must_change_password,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
